import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ActivitiesResponse, Activity } from '@features/activity/domain/activity.interface';
import { ExportParams } from '@features/activity/domain/export-params.interface';
import { DateFilterDto, Filters, FiltersDto, FiltersResponse } from '@features/activity/domain/filters.interface';
import { TollingRepository } from '@features/activity/domain/tolling.repository';
import { MICROSERVICES as microservices } from '@shared/constants/microservices';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class TollingService extends TollingRepository {
    readonly http = inject(HttpClient);

    private readonly endpoint = '/activities';

    getList(page = 1, pageSize = 30, filtersDto: FiltersDto): Observable<ActivitiesResponse> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        params = this.filtersQueryBuilder(params, filtersDto);
        const options = {
            params,
        };
        return this.http.get<ActivitiesResponse>(`${environment.url}${microservices.tolling}${this.endpoint}`, options);
    }

    getDetail(activityId: string): Observable<Activity> {
        return this.http.get<Activity>(`${environment.url}${microservices.tolling}${this.endpoint}/${activityId}`);
    }

    getFilters(filtersDto: FiltersDto): Observable<Filters> {
        let params = new HttpParams();

        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }
        const options = {
            params,
        };
        return this.http.get<FiltersResponse>(`${environment.url}${microservices.tolling}/activity-filters`, options).pipe(
            map(({ filters }: FiltersResponse) => {
                const {
                    // Todo:remove-roads
                    // roads,
                    vehicleTypes,
                    vehicles,
                } = filters;
                return {
                    // Todo:remove-roads
                    // roads,
                    vehicleTypes,
                    vehicles,
                };
            })
        );
    }

    private filtersQueryBuilder(params: HttpParams, filtersDto: FiltersDto) {
        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }

        if (filtersDto.dateRange.selected) {
            const range: DateFilterDto[] = [
                {
                    from: this.dateTransform(filtersDto.dateRange.range.from),
                    to: this.dateTransform(filtersDto.dateRange.range.to),
                },
            ];
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        if (filtersDto.datePeriod.selected) {
            const range: DateFilterDto[] = [];
            filtersDto.datePeriod.years.forEach((year) => {
                filtersDto.datePeriod.months.forEach((month) => {
                    const monthDate = new Date(new Date().getFullYear(), month.numMonth + 1, 0);
                    const numDays = monthDate.getDate();
                    range.push({
                        from: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}01`,
                        to: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}${numDays}`,
                    });
                });
            });
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        return params;
    }

    private dateTransform(value: string): string {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const newDate = `${year}${month}${day}`;
        return newDate;
    }

    exportToCsv(params: ExportParams): Observable<HttpResponse<Blob>> {
        let httpParams = new HttpParams();

        if (params.vehicle) {
            httpParams = httpParams.set('vehicle', params.vehicle);
        }

        if (params.activityGroupId) {
            httpParams = httpParams.set('activityGroupId', params.activityGroupId);
        }

        if (params.filtersDto) {
            httpParams = this.filtersQueryBuilder(httpParams, params.filtersDto);
        }

        if (params.activityIdList) {
            httpParams = httpParams.set('activityIds', params.activityIdList.join(','));
        }

        return this.http.get(`${environment.url}${microservices.tolling}${this.endpoint}/export`, {
            params: httpParams,
            observe: 'response',
            responseType: 'blob',
        });
    }
}
